import { useCallback, useState, type DragEvent } from "react";
import { toast } from "sonner";
import { create } from "zustand";

import { useUpload } from "@/hooks/use-upload";
import {
  collectEntries,
  MAX_UPLOAD_DEPTH,
  MAX_UPLOAD_FILES,
  type FileSystemEntryLike,
  type UploadItem,
} from "@/lib/dropzone-entries";

import { FILE_DND_TYPE } from "./FileTree";

/**
 * Whether a folder row has claimed the OS-file drag currently in flight.
 *
 * `GlobalDropzone` listens on `window`, so without this the full-screen "into
 * the atlas" overlay would slide over the exact row the user is aiming at and
 * then swallow the drop. The row says "this one's mine" on dragenter and lets
 * go on leave/drop; the dropzone reads the flag and stands down.
 */
export const useFolderDropClaim = create<{
  claimed: string | null;
  claim: (label: string) => void;
  release: () => void;
}>((set) => ({
  claimed: null,
  claim: (label) => set({ claimed: label }),
  release: () => set({ claimed: null }),
}));

/** An OS-file drag, as opposed to a file being dragged WITHIN the app. Both
 * look like drops; only one should upload anything. */
export function isNativeFileDrag(e: DragEvent): boolean {
  const types = e.dataTransfer?.types;
  if (!types) return false;
  return types.includes("Files") && !types.includes(FILE_DND_TYPE);
}

/** Prefix each dropped path with the folder it was dropped on, so the server's
 * existing `rel_path` handling files it there. A folder dragged in keeps its own
 * structure UNDER the destination, which is what dropping a folder into a folder
 * means everywhere else. */
export function within(folder: string, items: UploadItem[]): UploadItem[] {
  return items.map(({ file, relPath }) => ({
    file,
    relPath: [folder, relPath || file.name].filter(Boolean).join("/"),
  }));
}

/**
 * Drop OS files onto a folder row to upload them INTO that folder.
 *
 * Uploaded files are Ingested — that is intrinsic to where they came from — so
 * dropping onto `Markdown / Research` files them under `Research` in Ingested,
 * the same project folder seen from the category they actually belong to. The
 * hook says so rather than silently landing them somewhere else.
 */
export function useFolderUpload({
  folder,
  label,
  category,
}: {
  folder: string;
  label: string;
  category?: string | null;
}) {
  const upload = useUpload();
  const [over, setOver] = useState(false);

  const onDrop = useCallback(
    (e: DragEvent) => {
      if (!isNativeFileDrag(e)) return;
      // Both are load-bearing: `preventDefault` stops the browser navigating to
      // the file, `stopPropagation` stops `GlobalDropzone`'s window listener
      // uploading the same files a second time, to the vault root.
      e.preventDefault();
      e.stopPropagation();
      setOver(false);
      useFolderDropClaim.getState().release();

      const dt = e.dataTransfer;
      if (!dt) return;
      // The items list is live ONLY during dispatch, so snapshot the entries
      // synchronously and traverse them afterwards.
      const entries: FileSystemEntryLike[] = [];
      for (let i = 0; i < dt.items.length; i++) {
        const item = dt.items[i];
        if (!item || item.kind !== "file") continue;
        const entry = item.webkitGetAsEntry?.();
        if (entry) entries.push(entry as unknown as FileSystemEntryLike);
      }
      const files = entries.length ? null : Array.from(dt.files);

      void (async () => {
        let items: UploadItem[];
        if (files) {
          items = files.map((file) => ({ file }));
        } else {
          const collected = await collectEntries(entries);
          if (collected.truncated) {
            toast.warning(`Imported the first ${MAX_UPLOAD_FILES} files`, {
              description: "That drop is very large — import the rest separately.",
            });
          } else if (collected.depthTruncated) {
            toast.warning("Some deeply nested folders were skipped", {
              description: `Folders more than ${MAX_UPLOAD_DEPTH} levels deep weren't imported.`,
            });
          }
          items = collected.items;
        }
        if (!items.length) return;
        if (category && category !== "ingested") {
          toast(`Filing into ${folder || "your vault"}`, {
            description: "Uploaded files are Ingested — they keep that category wherever you drop them.",
          });
        }
        await upload(within(folder, items));
      })();
    },
    [category, folder, upload],
  );

  return {
    /** True while an OS-file drag is over this row — for the highlight. */
    over,
    props: {
      // dragenter/dragleave deliberately BUBBLE. `GlobalDropzone` counts them
      // on `window` to know whether a drag is still inside the page, and that
      // counter only balances if both halves reach it — swallowing the enter
      // while letting the leave through drives it negative, and the "into the
      // atlas" target then vanishes for the rest of the drag. Only `drop` is
      // stopped, because that one really must not happen twice.
      onDragEnter: (e: DragEvent) => {
        if (!isNativeFileDrag(e)) return;
        e.preventDefault();
        setOver(true);
        useFolderDropClaim.getState().claim(label);
      },
      onDragOver: (e: DragEvent) => {
        if (!isNativeFileDrag(e)) return;
        e.preventDefault();
        e.stopPropagation();
        e.dataTransfer.dropEffect = "copy";
        if (!over) setOver(true);
        // Re-claim on every frame: moving between two adjacent folder rows
        // fires the new row's enter BEFORE the old row's leave, so the leave
        // would otherwise release a claim the new row had already taken.
        if (useFolderDropClaim.getState().claimed !== label) {
          useFolderDropClaim.getState().claim(label);
        }
      },
      onDragLeave: (e: DragEvent) => {
        if (!isNativeFileDrag(e)) return;
        if ((e.currentTarget as Node).contains(e.relatedTarget as Node)) return;
        setOver(false);
        // Only give up the claim if it is still OURS (see onDragOver).
        if (useFolderDropClaim.getState().claimed === label) {
          useFolderDropClaim.getState().release();
        }
      },
      onDrop,
    },
  };
}
